import { DatePicker } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getDateFormat, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { ReceiptEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadReceipts,
  setAction,
  setReceiptFilter,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManageReceiptModalComponent,
  SearchReceiptDrawerComponent,
} from './components'

export const ReceiptPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { receipts, isReceiptsLoaded, receiptFilter, totalReceipts } =
    useSelector((state: RootState) => state.receipt)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [filter, setFilter] = React.useState(receiptFilter)
  const [selectedReceiptId, setSelectedReceiptId] = React.useState(-1)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadReceipts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setFilter(receiptFilter)
    dispatch(loadReceipts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('receipt.id', language),
        width: 50,
      },
      {
        title: (
          <>
            <span>{translate('receipt.createdDate', language)}</span>
            {isDesktopScreen && (
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                placeholder={[
                  translate('common.from', language),
                  translate('common.to', language),
                ]}
                value={[
                  moment(filter.createdDateFrom),
                  moment(filter.createdDateTo),
                ]}
                format="DD/MM/YYYY"
                onChange={(e) => {
                  setFilter({
                    ...filter,
                    createdDateFrom:
                      e && e.length === 2 && e[0]
                        ? e[0].toDate().getTime()
                        : filter.createdDateFrom,
                    createdDateTo:
                      e && e.length === 2 && e[1]
                        ? e[1].toDate().getTime()
                        : filter.createdDateTo,
                  })
                }}
              />
            )}
          </>
        ),
        width: 150,
        render: (_: any, receipt: ReceiptEntity) =>
          getDateFormat(receipt.createdDate),
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="receipt.customer"
            value={filter.customerName}
            onChange={(e) =>
              setFilter({ ...filter, customerName: e.target.value })
            }
          />
        ),
        width: 150,
        render: (_: any, receipt: ReceiptEntity) => receipt.card.customer?.name,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="receipt.amount"
            fromValue={filter.amountFrom}
            toValue={filter.amountTo}
            toKey="common.to"
            fromKey="common.from"
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                amountFrom: e.target.value,
              })
            }
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                amountTo: e.target.value,
              })
            }
          />
        ),
        width: 175,
        render: (_: any, receipt: ReceiptEntity) => (
          <NumberFormat suffixKey="common.moneyType" value={receipt.amount} />
        ),
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="receipt.reason"
            value={filter.reason}
            onChange={(e) => setFilter({ ...filter, reason: e.target.value })}
          />
        ),
        width: 150,
        render: (_: any, receipt: ReceiptEntity) => receipt.reason,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="receipt.creator"
            value={filter.creatorName}
            onChange={(e) =>
              setFilter({ ...filter, creatorName: e.target.value })
            }
          />
        ),
        width: 150,
        render: (_: any, receipt: ReceiptEntity) => receipt.creator.name,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setReceiptFilter({
                  ...filter,
                  pageIndex: 1,
                  amountFrom: getExpectNumber(
                    true,
                    filter.amountFrom,
                    filter.amountTo
                  ),
                  amountTo: getExpectNumber(
                    false,
                    filter.amountFrom,
                    filter.amountTo
                  ),
                })
              )
            }}
          />
        ),
        render: (_: any, receipt: ReceiptEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              setSelectedReceiptId(receipt.id)
              dispatch(setAction(ActionEnum.VIEW))
            }}
          />
        ),
        width: 150,
      },
    ],
    [language, isDesktopScreen, filter, dispatch]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && (
        <ManageReceiptModalComponent receiptId={selectedReceiptId} />
      )}
      <PageTitleComponent hasSearchDrawer />
      {management.showSearchDrawer && <SearchReceiptDrawerComponent />}
      <TableComponent
        paginationConfig={{
          current: receiptFilter.pageIndex,
          total: totalReceipts,
        }}
        data={receipts}
        columns={columns}
        loading={!isReceiptsLoaded}
        onChange={(pagination) => {
          const pageIndex = pagination.current || filter.pageIndex
          setFilter({ ...filter, pageIndex })
          dispatch(
            setReceiptFilter({
              ...filter,
              pageIndex,
            })
          )
        }}
      />
    </div>
  )
}
